import React, { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useCollectionBinding, useGallery } from '@vibesites/runtime/react';
import { EditableToken } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal, useStaggerReveal } from '@vibesites/theme-avenue/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-avenue/hooks/useRevealStyles';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function pickInteriorPhotos(gallery: PhotoGallery[], count: number): string[] {
  if (!gallery.length) return [];

  // Deduplicate the gallery by normalized URL first
  const seen = new Set<string>();
  const unique: { photo: PhotoGallery; score: number }[] = [];
  for (const photo of gallery) {
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ photo, score: scoreInterior(photo) });
  }

  // Sort by score descending
  unique.sort((a, b) => b.score - a.score);

  // Take the top-scored unique photos, then spread them out by
  // distributing evenly across the gallery order to avoid visually
  // similar adjacent photos (e.g. 3 kitchen shots in a row).
  const pool = unique.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    return pool.map((p) => p.photo.image!);
  }

  // Pick every Nth item to space them out visually
  const step = pool.length / count;
  const picked: string[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].photo.image!);
  }
  return picked;
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const label = useContentToken('floorplanslist.label');
  const title = useContentToken('floorplanslist.title');
  const subtitle = useContentToken('floorplanslist.subtitle');

  const capitalizedLabel = label
    ? label.replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const formattedLabel = capitalizedLabel ? `${capitalizedLabel} Bedroom Layouts` : null;

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const gallery = useGallery();

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // +1 for the "View All" card
  const photos = useMemo(
    () => pickInteriorPhotos(gallery, categories.length + 1),
    [gallery, categories.length],
  );

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      <div className="max-w-[1400px] mx-auto px-2 sm:px-3 lg:px-4">
        <div ref={headingRef} className="reveal-fade-up text-center mb-12">
          {formattedLabel && (
            <EditableToken path="floorplanslist.label" defaultField="property_bedrooms_range" as="p" className="font-body text-base sm:text-lg text-primary-foreground mb-3">
              {formattedLabel}
            </EditableToken>
          )}
          <EditableToken path="floorplanslist.title" defaultValue="Make Yourself at Home" as="h2" className="font-heading text-3xl sm:text-5xl font-bold text-[var(--vibe-color-text-on-primary)] mb-4 tracking-tight">
            {title}
          </EditableToken>
          <EditableToken path="floorplanslist.subtitle" defaultValue="Please select your desired layout below." as="p" className="font-body text-base sm:text-lg text-primary-foreground max-w-2xl mx-auto">
            {subtitle}
          </EditableToken>
        </div>

        {hasCategories ? (
          <div ref={gridRef} className="fp-grid grid grid-cols-2 md:grid-cols-3 lg:flex lg:flex-nowrap gap-3 md:gap-4">
            {/* "View All" card first */}
            <a
              data-reveal
              href="/floor-plans"
              className="reveal-fade-up fp-card group relative block aspect-[3/4] overflow-hidden cursor-pointer fp-card-item"
              style={{ flex: `1 1 0%`, minWidth: 0 } as React.CSSProperties}
            >
              {photos[0] && (
                <img
                  src={photos[0]}
                  alt="View all floor plans"
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                />
              )}
              <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-60" />
              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
                <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">ALL</span>
                <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">View All Layouts</span>
              </div>
            </a>

            {/* Bedroom category cards */}
            {categories.map((cat, i) => (
              <a
                key={cat.key}
                data-reveal
                href={`/floor-plans?bedroom=${cat.param}`}
                className="reveal-fade-up fp-card group relative block aspect-[3/4] overflow-hidden cursor-pointer fp-card-item"
                style={{ flex: `1 1 0%`, minWidth: 0 } as React.CSSProperties}
              >
                {photos[i + 1] && (
                  <img
                    src={photos[i + 1]}
                    alt={`${cat.label} floor plans`}
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-60" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
                  <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">
                    {cat.key === 'studio' ? 'S' : BED_ORDER.indexOf(cat.key).toString()}
                  </span>
                  <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
                    {cat.label} {cat.key === 'studio' ? '' : 'Layouts'}
                  </span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-white/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: rgba(0, 0, 0, 0.45);
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.6;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.label': {
      label: 'Section Label',
      type: 'text',
      defaultField: 'property_bedrooms_range',
      description: 'Label above the title showing bedroom range (e.g., "1 - 3 Bedroom Layouts").',
      suggestedFields: ['property_bedrooms_range'],
    },
    'floorplanslist.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Make Yourself at Home',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'Please select your desired layout below.',
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
